import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatusPill } from "@aeriosiq/ui";
// SB6-ANA-UI-01 — shared compact-control presets. The AI-tab status filter
// (Pending/Dismissed/Promoted/All) was bare `size="small"` MUI; rows carried
// 15px primary text. Both move onto the defect-drawer dense scale.
import {
  compactButtonXsSx,
  compactToggleXsSx,
  typography as uiTypography,
} from "@aeriosiq/ui";
import { type AiFinding, type AiFindingStateFilter } from "@aeriosiq/types";
import {
  useBulkDismissFindingsMutation,
  useFindingsQuery,
  useMergeFindingsMutation,
  useUndismissFindingMutation,
  useUndoableDismissFindingMutation,
  useUndoablePromoteFindingMutation,
  statusToStatusPill,
} from "./queries.js";
// TRIAGE-02 (IMP-02) — shared preset dismiss-reason chips + the global
// undo trigger for the "Ctrl+Z to undo" snackbar action.
import { DISMISS_REASON_PRESETS } from "../analysis/triage/dismissReasons.js";
import { useUndoStackStore } from "../analysis/state/undoStackStore.js";
import { useCanEditResource } from "@aeriosiq/ui";
// R58-L-11 — segmented state filter (pending / dismissed / promoted / all).
// Bound to the `state` query param wired by R58-L-07 into `useFindingsQuery`.
// Replaces the legacy multi-status Autocomplete; the two collide on the
// same filter axis (PR #58 walkthrough — single segmented control is the
// committed UX, see `_STORIES/lyse/R58-L-07-list-widget.md` AC6/AC7/AC8).
type StateFilterValue = AiFindingStateFilter | "all";
const STATE_FILTER_OPTIONS: readonly {
  value: StateFilterValue;
  label: string;
}[] = [
  { value: "pending", label: "Pending" },
  { value: "dismissed", label: "Dismissed" },
  { value: "promoted", label: "Promoted" },
  { value: "all", label: "All" },
];
import { CataloguePopover } from "../catalogues/index.js";
import {
  denyReasonTooltip,
  type DefectEditPermission,
} from "../analysis/editPermission.js";
// SB6-ANA-PROG-01 — grading gate. Grading actions (promote / dismiss /
// merge / undismiss) are disabled until the dataset is `dataPointReady`,
// and the keyboard/action layer no-ops before then (AC3).
import {
  GRADING_NOT_READY_TOOLTIP,
  isGradingReady,
  useGradingReady,
} from "../analysis/state/analysisReadinessStore.js";
import { useSelectionStore } from "../analysis/state/selectionStore.js";
import { useAnalysisFilterStore } from "../analysis/state/analysisFilterStore.js";
import { useAnalysisFilter } from "../analysis/useAnalysisFilter.js";
// SB6-ANA-FILT-07 — the committed AI-class confidence filter, gated to the
// AI kind exactly as the data-point counts are, so the AI-findings List
// narrows server-side per finding (not just per image).
import { useEffectiveAiClassFilter } from "../analysis/useEffectiveAiClassFilter.js";
// ANA-R1-04 — shared raw-class-name → model display-name lookup so the List
// rows render the canonical `ML_*` display name, matching the AI filter module.
import { useClassDisplayNames } from "../analysis/useClassDisplayNames.js";
// R58-L-07 AC5 (SB5 Track C.6) — TanStack Virtual row-windowing primitive.
// Closes the deferred AC5 from PR #76; shared between FindingsPanel and
// DefectsPanel. The primitive activates only when `scrollFromParent` is
// true (i.e. inside the analysis ListWidget shell); the legacy
// non-virtualised path is preserved so the existing unit tests, which
// mount FindingsPanel directly without scrollFromParent, continue to
// pass without modification.
import { VirtualisedRowList } from "../analysis/list/VirtualisedRowList.js";

interface Props {
  inspectionId: string;
  onSelect?: (finding: AiFinding) => void;
  /**
   * Story 020 AC12 — D-5 role/phase gate. When omitted the panel
   * is left writeable (legacy + unit-test default). Story 020's
   * Reclassify menu item is `aria-disabled` and inert when gated.
   */
  editPermission?: DefectEditPermission;
  /**
   * R58-L-07 AC2/AC3 — when set, scopes the findings query to a
   * single image. Wires the `imageId` query-param added in lyse/v1@5.6.0.
   */
  imageIdScope?: string | null;
  /**
   * R58-L-07 AC1 — when true, the panel renders flush (no inner
   * `overflow: auto`) so the parent ListWidget body owns the only
   * scroll container.
   */
  scrollFromParent?: boolean;
  /**
   * R58-L-07 AC4 — count badge feed-up. Fires whenever the query's
   * `total` changes so the parent ListWidget can render the
   * `<current> / <dataset>` badge.
   */
  onCountChange?: (n: number) => void;
}

function formatRelative(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Math.max(0, now.getTime() - t);
  const min = Math.round(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${String(min)}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${String(h)}h ago`;
  const d = Math.round(h / 24);
  return `${String(d)}d ago`;
}

function FindingsHeader({
  count,
  stateFilter,
  onStateFilterChange,
  mergeCount,
  onOpenMerge,
  bulkDismissCount,
  onOpenBulkDismiss,
}: {
  count: number;
  stateFilter: StateFilterValue;
  onStateFilterChange: (next: StateFilterValue) => void;
  mergeCount: number;
  onOpenMerge: (anchor: HTMLElement) => void;
  /** R58-L-11 AC3 — number of currently-selected pending findings.
   *  When > 0 the "Dismiss selected (n)" bulk action is shown. */
  bulkDismissCount: number;
  onOpenBulkDismiss: (anchor: HTMLElement) => void;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        px: 2,
        py: 1,
        alignItems: "center",
        borderBottom: 1,
        borderColor: "divider",
        flexWrap: "wrap",
      }}
    >
      {/* UI-01 follow-up (2026-06-05) — subtitle1 read oversized next to the
          XS controls; drop to subtitle2, the same scale as the widget's
          top-row "Analysis" title. */}
      <Typography variant="subtitle2" component="h2" sx={{ flex: 1 }}>
        Findings ({count})
      </Typography>
      {bulkDismissCount > 0 ? (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          // ANA-R1-11 — bulk action conforms to the agreed analysis-surface
          // action-button style (shared `compactButtonXsSx`, the same 24px XS
          // scale as the per-row Promote/Dismiss below).
          sx={compactButtonXsSx}
          onClick={(e) => {
            onOpenBulkDismiss(e.currentTarget);
          }}
          aria-label={`Dismiss selected (${String(bulkDismissCount)})`}
          data-testid="findings-bulk-dismiss-button"
        >
          Dismiss selected ({bulkDismissCount})
        </Button>
      ) : null}
      {mergeCount >= 2 ? (
        <Button
          size="small"
          variant="contained"
          // ANA-R1-11 — bulk action conforms to the agreed analysis-surface
          // action-button style (shared `compactButtonXsSx`).
          sx={compactButtonXsSx}
          onClick={(e) => {
            onOpenMerge(e.currentTarget);
          }}
          aria-label={`Merge selected (${String(mergeCount)})`}
        >
          Merge selected ({mergeCount})
        </Button>
      ) : null}
      {/*
        R58-L-11 / R58-L-07 AC6/AC7/AC8 — segmented state filter. Bound to
        the `state` query param wired by R58-L-07. Replaces the legacy
        multi-status Autocomplete (different axis: the Autocomplete was the
        4-value contract `status[]`, this is the 3-value derived `state`
        which collapses `pending`, `dismissed`, `promoted` (`promoted`
        joins on `promoted_to_defect_id IS NOT NULL`). The `all` option
        passes no `state` param so the BE returns every state.
      */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={stateFilter}
        onChange={(_e, next: StateFilterValue | null) => {
          if (!next) return;
          onStateFilterChange(next);
        }}
        aria-label="Finding state filter"
        data-testid="findings-state-filter"
      >
        {STATE_FILTER_OPTIONS.map((opt) => (
          <ToggleButton
            key={opt.value}
            value={opt.value}
            aria-label={opt.label}
            // UI-01 follow-up — XS (24px chip-height), same row treatment as
            // the ListWidget scope toggle.
            sx={compactToggleXsSx}
          >
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

// R58-L-11 — URL search-param key for the persisted state filter.
const STATE_FILTER_PARAM = "aiState";
// ANA-R1-02 — the AI-findings List now defaults to ALL states, not
// `pending`. The 2026-06-06 walkthrough demanded the List, Tiles and Viewer
// agree on which findings exist on an image (*"all three widget must show the
// same amount of information"* [00:15:37]; *"in the list we show everything"*
// [00:15:25]). The Tiles + Viewer never narrowed by state, so a `pending`
// default made the List count diverge from the tile + bbox counts out of the
// box (AC2/AC3 — no hidden per-widget default). The segmented control still
// lets the analyst narrow to Pending/Dismissed/Promoted explicitly; it is a
// List-only view refinement, not a definition of the effective set.
const DEFAULT_STATE_FILTER: StateFilterValue = "all";

function isStateFilterValue(v: string | null): v is StateFilterValue {
  return (
    v === "pending" || v === "dismissed" || v === "promoted" || v === "all"
  );
}

export function FindingsPanel({
  inspectionId,
  onSelect,
  editPermission,
  imageIdScope,
  scrollFromParent,
  onCountChange,
}: Props) {
  // R58-L-11 — segmented state filter, persisted in the URL so the
  // analyst's view survives a page reload and is link-shareable. Default
  // `pending` per R58-L-07 §"Decisions locked" — the analyst lands on
  // unresolved AI findings out of the box.
  //
  // Local state mirrors the URL param so React renders synchronously
  // before `useSearchParams` settles; a `useEffect` writes back on
  // change. We avoid going through `useSearchParams` directly because
  // FindingsPanel runs in non-router contexts during component tests
  // (the unit test in FindingsPanel.test.tsx mounts without a Router).
  const [stateFilter, setStateFilter] = useState<StateFilterValue>(() => {
    if (typeof window === "undefined") return DEFAULT_STATE_FILTER;
    const url = new URL(window.location.href);
    const v = url.searchParams.get(STATE_FILTER_PARAM);
    return isStateFilterValue(v) ? v : DEFAULT_STATE_FILTER;
  });
  const handleStateFilterChange = (next: StateFilterValue) => {
    setStateFilter(next);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (next === DEFAULT_STATE_FILTER) {
      url.searchParams.delete(STATE_FILTER_PARAM);
    } else {
      url.searchParams.set(STATE_FILTER_PARAM, next);
    }
    window.history.replaceState(null, "", url.toString());
  };
  // SB6-ANA-PROG-01 — the readiness gate is ANDed with the existing D-5
  // permission gate. Grading stays disabled (with an explanatory tooltip)
  // until the first data point is ready; navigation/viewing are unaffected.
  const gradingReady = useGradingReady();
  const permissionAllowed = editPermission?.allowed !== false;
  const canEdit = permissionAllowed && gradingReady;
  const denyTooltip =
    editPermission && !editPermission.allowed
      ? denyReasonTooltip(editPermission.reason)
      : !gradingReady && permissionAllowed
        ? GRADING_NOT_READY_TOOLTIP
        : null;
  // R58-L-07 — propagate the imageId scope to the query (lyse/v1@5.6.0).
  // R58-L-05 — propagate the defect text-search term (lyse/v1@5.8.0).
  // For AI findings the BE ILIKEs against `ai_class_name`; for defects
  // it ILIKEs against the catalogue label in DE or EN. Both consumer
  // panels share the same store-level `q` so a single search input
  // narrows both tabs in lock-step.
  // R58-L-11 — the `state` query param (also wired by R58-L-07) is now
  // driven by the segmented control. `all` passes no `state` so the BE
  // returns every state.
  const filterQ = useAnalysisFilterStore((s) => s.q);
  // R58-L-05 Track C.4 — defectClassIds AND-axis from the new typeahead.
  const { defectClassIds } = useAnalysisFilter();
  // SB6-ANA-FILT-07 — committed AI-class confidence filter (or null when it
  // must not narrow). Threaded into the query params + key so a debounced
  // threshold change refetches and the AI tab + count badge reflect only
  // passing findings.
  const aiClassFilter = useEffectiveAiClassFilter();
  // R58-L-07 AC5 — pageSize=2000 so the 709-row worst case fits in a
  // single response and virtualisation has the full set to window over.
  // Future enhancement: swap to `useInfiniteQuery` once we cross the
  // 2000-row threshold (cursor pagination is already wired BE-side via
  // `nextCursor` on GetFindingsResponse). Bumping the page size only
  // for the analysis panel context (when scrollFromParent is true)
  // keeps existing direct-mount unit tests (which still use the BE
  // default page size) unaffected.
  const query = useFindingsQuery(inspectionId, {
    ...(imageIdScope ? { imageId: imageIdScope } : {}),
    ...(filterQ ? { q: filterQ } : {}),
    ...(stateFilter !== "all" ? { state: stateFilter } : {}),
    ...(defectClassIds.length > 0
      ? { defectClassIds: [...defectClassIds] }
      : {}),
    ...(aiClassFilter ? { aiClassFilter } : {}),
    ...(scrollFromParent ? { pageSize: 2000 } : {}),
  });
  const items = useMemo<AiFinding[]>(
    () => query.data?.items ?? [],
    [query.data?.items],
  );
  const total = query.data?.total ?? 0;

  // ANA-R1-04 — the shared raw-class-name → model display-name lookup. The
  // List rows render the canonical display name (the same `ML_*` the AI Filter
  // Module shows), never the raw German `class_name`. Unresolvable classes
  // fall back to the raw name, marked unobtrusive (see ANA-R1-05).
  const classNames = useClassDisplayNames(inspectionId);

  // R58-L-07 AC4 — surface the query's total to the parent so the
  // ListWidget can compose the `<current> / <dataset>` badge.
  useEffect(() => {
    if (onCountChange) onCountChange(total);
  }, [onCountChange, total]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // R58-L-13 AC7 — mirror the global selection store id into local
  // `selectedId` so a Viewer-bbox click highlights the matching row
  // here even when the click never traversed the panel. The kind hint
  // is consulted (skip when activeKind === "defect" — that belongs to
  // DefectsPanel) so a defect activation doesn't accidentally light an
  // unrelated AI row of the same id (UUIDs are universally unique, but
  // the guard keeps the intent explicit).
  const externalActiveId = useSelectionStore((s) => s.activeFindingId);
  const externalActiveKind = useSelectionStore((s) => s.activeKind);
  useEffect(() => {
    if (externalActiveKind !== "ai") return;
    if (externalActiveId && externalActiveId !== selectedId) {
      setSelectedId(externalActiveId);
    }
  }, [externalActiveId, externalActiveKind, selectedId]);
  // Story 064 AC6 — undoable wrapper. Pushes a `createDefectFromFinding`
  // entry onto the per-image undo stack and writes an audit-log row.
  // `Ctrl+Z` in the parent window reverts the promotion (finding status
  // flips back to `pending`, defect is removed).
  const promote = useUndoablePromoteFindingMutation(inspectionId);
  // TRIAGE-02 (Q3 decided) — single dismiss is INSTANT + undoable: the
  // undoable wrapper flips the row optimistically, appends the audit row
  // and pushes a global undo entry (Ctrl+Z = undismiss). The reason
  // popover survives behind Shift+Click / Shift+X only.
  const dismiss = useUndoableDismissFindingMutation(inspectionId);
  const merge = useMergeFindingsMutation(inspectionId);
  // R58-L-11 AC3 — bulk dismiss against the currently-selected rows.
  const bulkDismiss = useBulkDismissFindingsMutation(inspectionId);
  // R58-L-11 AC5 — undismiss (role-gated; the row-level button hides
  // when `useCanEditResource('finding.undismiss')` is false).
  const undismiss = useUndismissFindingMutation(inspectionId);
  const canUndismiss = useCanEditResource("finding.undismiss");
  const [dismissPopover, setDismissPopover] = useState<{
    anchor: HTMLElement;
    findingId: string;
  } | null>(null);
  const [dismissReason, setDismissReason] = useState("");
  const dismissInputRef = useRef<HTMLInputElement | null>(null);

  // Story 005b — multi-select for merge.
  const [selectedForMerge, setSelectedForMerge] = useState<Set<string>>(
    new Set(),
  );
  const [mergePopover, setMergePopover] = useState<HTMLElement | null>(null);
  const toggleMergeSelection = (id: string) => {
    setSelectedForMerge((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // R58-L-11 AC3 — bulk-dismiss dialog state. Reads selected ids from
  // the existing tile/finding selection store (Q7 locked decision).
  const selectionStore = useSelectionStore((s) => s.selectedFindingIds);
  const clearSelectionStore = useSelectionStore((s) => s.reset);
  const [bulkDismissPopover, setBulkDismissPopover] =
    useState<HTMLElement | null>(null);
  const [bulkDismissReason, setBulkDismissReason] = useState("");
  const bulkDismissInputRef = useRef<HTMLInputElement | null>(null);
  // Restrict the bulk-dismiss target set to the rows currently rendered
  // in the FindingsPanel + status `'pending'` (the BE rejects 'converted';
  // already-dismissed rows pass through as idempotent no-ops, but it's
  // cleaner UX to scope to pending only).
  const bulkDismissableIds = useMemo(() => {
    const ids: string[] = [];
    for (const f of items) {
      if (f.status === "pending" && selectionStore.has(f.id)) ids.push(f.id);
    }
    return ids;
  }, [items, selectionStore]);

  // R58-L-11 AC5 — undismiss confirm dialog state.
  const [undismissConfirm, setUndismissConfirm] = useState<{
    anchor: HTMLElement;
    findingId: string;
    aiClassName: string;
  } | null>(null);

  const [snack, setSnack] = useState<
    | { kind: "promoteSuccess"; defectId: string }
    | { kind: "dismissSuccess" }
    | { kind: "bulkDismissSuccess"; count: number }
    | { kind: "undismissSuccess" }
    | { kind: "mergeSuccess"; defectId: string }
    | {
        kind: "error";
        action: "promote" | "dismiss" | "merge" | "bulkDismiss" | "undismiss";
        errorId: string;
      }
    | null
  >(null);

  // Story 020 — Reclassify context menu + Catalogue popover for AI
  // findings. Source is `aiFinding`, which surfaces AC13's
  // "Apply mapping for future findings" checkbox.
  const [ctxMenu, setCtxMenu] = useState<{
    anchorPos: { left: number; top: number };
    rowEl: HTMLElement;
    findingId: string;
    aiClassName: string;
    /** AIMAP-03 — authoritative class key for the ai-map suggest endpoint. */
    classUuid: string;
  } | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverSelection, setPopoverSelection] = useState<{
    ids: readonly string[];
    aiClassName: string | undefined;
    classUuid: string | undefined;
  }>({ ids: [], aiClassName: undefined, classUuid: undefined });

  const isError = query.isError;
  const errorId =
    (query.error as { errorId?: string } | undefined)?.errorId ?? "unknown";
  const isPending = query.isPending;
  const isEmpty = !isPending && !isError && items.length === 0;

  const handlePromote = (findingId: string) => {
    // SB6-ANA-PROG-01 — gate the action layer (defense-in-depth alongside
    // the disabled button + keyboard guard).
    if (!isGradingReady()) return;
    // Story 064 AC6 — the undoable wrapper needs imageId (to key the
    // per-image undo stack) and dataAcquisitionId (for the audit row).
    // imageId comes off the AiFinding row itself; dataAcquisitionId is
    // the panel's inspectionId.
    const finding = items.find((f) => f.id === findingId);
    if (!finding) return;
    promote.mutate(
      {
        findingId,
        imageId: finding.imageId,
        dataAcquisitionId: inspectionId,
      },
      {
        onSuccess: (data) => {
          setSnack({ kind: "promoteSuccess", defectId: data.defectId });
        },
        onError: (err) => {
          setSnack({
            kind: "error",
            action: "promote",
            errorId: err.errorId ?? "unknown",
          });
        },
      },
    );
  };

  // TRIAGE-02 — shared dismissal dispatch (instant path AND the
  // Shift+Click reason path both land here; only `reason` differs).
  const runDismiss = (findingId: string, reason?: string) => {
    if (!isGradingReady()) return;
    const finding = items.find((f) => f.id === findingId);
    if (!finding) return;
    const trimmed = reason?.trim();
    dismiss.mutate(
      {
        findingId,
        ...(trimmed ? { reason: trimmed } : {}),
        imageId: finding.imageId,
        dataAcquisitionId: inspectionId,
      },
      {
        onSuccess: () => {
          setSnack({ kind: "dismissSuccess" });
        },
        onError: (err) => {
          setSnack({
            kind: "error",
            action: "dismiss",
            errorId: err.errorId ?? "unknown",
          });
        },
      },
    );
  };

  const openDismissPopover = (anchor: HTMLElement, findingId: string) => {
    setDismissReason("");
    setDismissPopover({ anchor, findingId });
  };
  // Focus the reason field once the popover has rendered. We avoid the
  // `autoFocus` prop (jsx-a11y/no-autofocus) and do it imperatively.
  useEffect(() => {
    if (!dismissPopover) return;
    const t = setTimeout(() => {
      dismissInputRef.current?.focus();
    }, 50);
    return () => {
      clearTimeout(t);
    };
  }, [dismissPopover]);
  const closeDismissPopover = () => {
    setDismissPopover(null);
  };
  /** Submit the reason popover — `reasonOverride` is the chip path
   *  (chip click = submit immediately, IMP-02). */
  const submitDismiss = (reasonOverride?: string) => {
    if (!dismissPopover) return;
    const findingId = dismissPopover.findingId;
    const reason = (reasonOverride ?? dismissReason).trim();
    closeDismissPopover();
    runDismiss(findingId, reason || undefined);
  };

  const submitMerge = () => {
    setMergePopover(null);
    if (!isGradingReady()) return;
    const findingIds = Array.from(selectedForMerge);
    if (findingIds.length < 2) return;
    merge.mutate(
      { findingIds },
      {
        onSuccess: (data) => {
          setSnack({ kind: "mergeSuccess", defectId: data.defectId });
          setSelectedForMerge(new Set());
        },
        onError: (err) => {
          setSnack({
            kind: "error",
            action: "merge",
            errorId: err.errorId ?? "unknown",
          });
        },
      },
    );
  };

  // R58-L-11 AC3 — bulk dismiss confirm.
  const openBulkDismissPopover = (anchor: HTMLElement) => {
    setBulkDismissReason("");
    setBulkDismissPopover(anchor);
  };
  const closeBulkDismissPopover = () => {
    setBulkDismissPopover(null);
  };
  useEffect(() => {
    if (!bulkDismissPopover) return;
    const t = setTimeout(() => {
      bulkDismissInputRef.current?.focus();
    }, 50);
    return () => {
      clearTimeout(t);
    };
  }, [bulkDismissPopover]);
  const submitBulkDismiss = () => {
    if (!isGradingReady()) return;
    const findingIds = bulkDismissableIds;
    const reason = bulkDismissReason.trim();
    if (findingIds.length === 0 || reason.length === 0) return;
    closeBulkDismissPopover();
    bulkDismiss.mutate(
      { findingIds, reason },
      {
        onSuccess: (data) => {
          setSnack({
            kind: "bulkDismissSuccess",
            count: data.dismissed.length,
          });
          // Q7 — clear the selection on success so the analyst doesn't
          // see a stale "Dismiss selected (n)" button.
          clearSelectionStore();
        },
        onError: (err) => {
          // Q7 — preserve the selection on 4xx/5xx so the user can retry.
          setSnack({
            kind: "error",
            action: "bulkDismiss",
            errorId: err.errorId ?? "unknown",
          });
        },
      },
    );
  };

  // R58-L-11 AC5 — undismiss confirm.
  const openUndismissConfirm = (
    anchor: HTMLElement,
    findingId: string,
    aiClassName: string,
  ) => {
    setUndismissConfirm({ anchor, findingId, aiClassName });
  };
  const closeUndismissConfirm = () => {
    setUndismissConfirm(null);
  };
  const submitUndismiss = () => {
    if (!undismissConfirm) return;
    if (!isGradingReady()) return;
    const findingId = undismissConfirm.findingId;
    closeUndismissConfirm();
    undismiss.mutate(
      { findingId },
      {
        onSuccess: () => {
          setSnack({ kind: "undismissSuccess" });
        },
        onError: (err) => {
          setSnack({
            kind: "error",
            action: "undismiss",
            errorId: err.errorId ?? "unknown",
          });
        },
      },
    );
  };

  // TRIAGE-02 — the legacy panel-scoped `p` (promote) / `d` (dismiss)
  // keydown handler is RETIRED. The global triage layer (audit 3.2 /
  // IMP-01, `analysis/triage/useTriageHotkeys.ts`) owns the keyboard
  // decisions now (`a`/`Enter` promote, `x`/`Del` dismiss, `Shift+X`
  // reason) — and the old `d` binding collided with the DrawOverlay's
  // draw-mode toggle, firing both on one press.

  // R58-L-07 AC5 (Track C.6) — single row renderer reused by both the
  // virtualised path (inside the analysis ListWidget) and the legacy
  // non-virtualised path (direct-mount + unit tests). Keeping one
  // implementation means a11y / interaction semantics can't drift
  // between the two modes.
  function renderFindingRow(f: AiFinding) {
    // ANA-R1-04 — render the model display name everywhere; raw class_name is
    // never user-facing. Unresolved classes keep the raw name, dimmed.
    const cls = classNames.resolve(f.aiClassName);
    return (
      <Box
        key={f.id}
        data-finding-row={f.id}
        onContextMenu={(e) => {
          e.preventDefault();
          setCtxMenu({
            anchorPos: { left: e.clientX, top: e.clientY },
            rowEl: e.currentTarget,
            findingId: f.id,
            aiClassName: cls.display,
            classUuid: f.classUuid,
          });
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {f.status === "pending" ? (
          <Checkbox
            size="small"
            checked={selectedForMerge.has(f.id)}
            onChange={() => {
              toggleMergeSelection(f.id);
            }}
            inputProps={{
              "aria-label": `Select ${cls.display} for merge`,
            }}
            sx={{ ml: 1, flexShrink: 0 }}
          />
        ) : null}
        <ListItemButton
          selected={selectedId === f.id}
          onClick={() => {
            setSelectedId(f.id);
            onSelect?.(f);
          }}
          data-finding-id={f.id}
          // `minWidth: 0` is load-bearing: without it the button (a flex item)
          // refuses to shrink below its content width, so the nowrap class
          // name never clips and the row would grow / reflow. (SB6-ANA-UI-01)
          sx={{ flex: 1, minWidth: 0, alignItems: "flex-start", py: 0.75 }}
        >
          <ListItemText
            primary={
              // SB6-ANA-UI-01 — primary text drops 15px/600 (`body2`) to the
              // defect-drawer tier-2 13px/600 (`caption-lg`). The class name
              // ellipsizes on ONE line (full name in the title tooltip)
              // instead of wrapping, so the row layout stays stable at the
              // widget's minimum width (AC3 — rows stop rearranging). The
              // confidence % stays fixed-width to the right.
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minWidth: 0, width: "100%" }}
              >
                <Tooltip title={cls.display} enterDelay={500}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: uiTypography.size["caption-lg"],
                      fontWeight: 600,
                      minWidth: 0,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      // ANA-R1-04 — an unresolved class (raw name shown as a
                      // fallback) reads muted + italic so it's visibly the odd
                      // one out, not hidden (ANA-R1-05 investigates the cause).
                      ...(cls.resolved
                        ? {}
                        : { fontStyle: "italic", color: "text.secondary" }),
                    }}
                    data-finding-class-name={f.id}
                    data-class-unresolved={cls.resolved ? undefined : "true"}
                  >
                    {cls.display}
                  </Typography>
                </Tooltip>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
                >
                  {Math.round(f.confidence * 100)}%
                </Typography>
              </Stack>
            }
            secondary={
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.5 }}
              >
                <StatusPill
                  status={statusToStatusPill(f.status)}
                  label={f.status}
                  dense
                />
                <Typography variant="caption" color="text.secondary">
                  {formatRelative(f.processedAt)}
                </Typography>
              </Stack>
            }
            slotProps={{
              // SB6-ANA-UI-01 — render primary as a flex container so the
              // class-name Typography's `flex:1 + overflow:hidden` ellipsis
              // resolves against the row width (the default `<span>` would
              // shrink-wrap and never clip). `min-width:0` lets the flex item
              // actually shrink below its content width.
              primary: {
                component: "div",
                sx: { display: "flex", minWidth: 0 },
              },
              secondary: { component: "div" },
            }}
          />
        </ListItemButton>
        {f.status === "pending" ? (
          // SB6-ANA-PROG-01 — wrap the grading actions so the disabled
          // state (readiness gate or D-5 denial) carries an accessible
          // tooltip explaining why. `disableHoverListener` keeps the
          // tooltip silent when actions are enabled.
          <Tooltip
            title={denyTooltip ?? ""}
            disableHoverListener={canEdit}
            disableFocusListener={canEdit}
            disableTouchListener={canEdit}
          >
            <Stack direction="row" spacing={0.5} sx={{ mx: 1, flexShrink: 0 }}>
              {/* UI-01 follow-up — row actions at the same 24px XS scale as
                  the filter toggles above (Christian 2026-06-05). */}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  handlePromote(f.id);
                }}
                disabled={promote.isPending || !canEdit}
                aria-label={`Promote ${cls.display} finding`}
                sx={compactButtonXsSx}
              >
                Promote
              </Button>
              {/* TRIAGE-02 (Q3) — click = INSTANT undoable dismiss;
                  Shift+Click keeps the optional reason popover. */}
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  if (e.shiftKey) {
                    openDismissPopover(e.currentTarget, f.id);
                  } else {
                    runDismiss(f.id);
                  }
                }}
                disabled={!canEdit}
                aria-label={`Dismiss ${cls.display} finding`}
                title="Dismiss — Shift+Click to add a reason"
                sx={compactButtonXsSx}
              >
                Dismiss
              </Button>
            </Stack>
          </Tooltip>
        ) : f.status === "dismissed" && canUndismiss ? (
          // R58-L-11 AC5 — undismiss action on dismissed rows.
          // Hidden when the role cell denies `finding.undismiss`
          // (pilot, back_office). Click opens a confirm dialog.
          <Tooltip
            title={denyTooltip ?? ""}
            disableHoverListener={canEdit}
            disableFocusListener={canEdit}
            disableTouchListener={canEdit}
          >
            <Stack direction="row" spacing={0.5} sx={{ mx: 1, flexShrink: 0 }}>
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  openUndismissConfirm(e.currentTarget, f.id, cls.display);
                }}
                disabled={undismiss.isPending || !canEdit}
                aria-label={`Undismiss ${cls.display} finding`}
                data-testid={`finding-undismiss-${f.id}`}
                // UI-01 follow-up — same XS scale as Promote/Dismiss.
                sx={compactButtonXsSx}
              >
                Undismiss
              </Button>
            </Stack>
          </Tooltip>
        ) : null}
      </Box>
    );
  }

  return (
    // role="region" + aria-label is what the 004/005/005a/005b/007
    // specs match via `getByRole('region', { name: /findings/i })`.
    // The outer WidgetSlot wrapper (aria-label "List") doesn't collide
    // because /findings/i doesn't match "List". ListWidget conditionally
    // renders only the active tab's panel, so FindingsPanel and
    // DefectsPanel never co-exist in the DOM — no nested-region or
    // strict-mode collision between the two inner panels either.
    //
    // R58-L-07 AC5 (Track C.6) — When `scrollFromParent` is true the
    // panel itself becomes the scroll container (via VirtualisedRowList);
    // the Paper layout switches to flex column so the virtualised list
    // can grow into the available height. The parent ListWidget body
    // runs `overflow: hidden` in that case so AC1 holds.
    <Paper
      variant="outlined"
      role="region"
      aria-label="Findings"
      sx={
        scrollFromParent
          ? {
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }
          : { overflow: "hidden" }
      }
    >
      <FindingsHeader
        count={total}
        stateFilter={stateFilter}
        onStateFilterChange={handleStateFilterChange}
        mergeCount={selectedForMerge.size}
        onOpenMerge={(anchor) => {
          setMergePopover(anchor);
        }}
        bulkDismissCount={bulkDismissableIds.length}
        onOpenBulkDismiss={openBulkDismissPopover}
      />

      {isPending ? (
        <Stack spacing={1} sx={{ p: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={56} />
          ))}
        </Stack>
      ) : isError ? (
        <Stack
          spacing={1}
          sx={{ p: 3 }}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <Typography variant="subtitle1" component="h3">
            Couldn&apos;t load findings
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            errorId: {errorId}
          </Typography>
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                void query.refetch();
              }}
            >
              Retry
            </Button>
          </Box>
        </Stack>
      ) : isEmpty ? (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">No findings yet</Typography>
        </Box>
      ) : scrollFromParent ? (
        // R58-L-07 AC5 (Track C.6) — virtualised path. Used inside the
        // analysis ListWidget shell, where datasets can reach 709 rows
        // (the real-world worst case). Rows are windowed via TanStack
        // Virtual; only the in-viewport ~10–20 rows are mounted to the
        // DOM at a time. Row identity is by `finding.id` so React keys
        // stay stable through scrolling, and the select / merge sets
        // hold ids (not refs) so selection survives row recycling.
        //
        // Container layout: when virtualised the panel becomes the
        // scroll container; the parent ListWidget body keeps
        // `overflow: hidden` so AC1 (single-scrollbar) still holds.
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <VirtualisedRowList
            items={items}
            getRowId={(f) => f.id}
            // SB6-ANA-UI-01 — tighter rows after the 15px→13px primary text
            // drop, single-line (no-wrap) class name, and `py: 0.75` on the
            // row button. Pending rows with action buttons now measure to
            // ~56px; non-pending rows to ~48px. 52px is the new midpoint
            // estimate; `measureElement` in the primitive converges to the
            // real per-row values on first paint (this just seeds the initial
            // window so scroll position is close before measurement).
            estimateSize={52}
            overscan={8}
            testId="findings-virtual-list"
            ariaLabel="AI findings list"
            renderRow={(f) => renderFindingRow(f)}
          />
        </Box>
      ) : (
        // Region of clickable rows. The select-row button and the Promote
        // button are siblings (not nested) so axe's nested-interactive rule
        // stays clean. We don't wrap in <ul role="list"> because MUI's
        // ListItemButton forces role="button", and role=button as a direct
        // child of role=list trips a different axe rule.
        //
        // Legacy non-virtualised path. Retained so existing direct-mount
        // FindingsPanel unit tests + non-shell consumers see the original
        // layout. Inner `maxHeight: 480, overflowY: auto` provides the
        // standalone scroll container.
        <Box sx={{ maxHeight: 480, overflowY: "auto" }}>
          {items.map((f) => renderFindingRow(f))}
        </Box>
      )}

      {snack ? (
        <Snackbar
          open
          autoHideDuration={snack.kind === "error" ? 6000 : 3000}
          onClose={() => {
            setSnack(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          {snack.kind === "promoteSuccess" ? (
            <Alert
              severity="success"
              variant="filled"
              onClose={() => {
                setSnack(null);
              }}
            >
              Promoted — defect {snack.defectId}
            </Alert>
          ) : snack.kind === "dismissSuccess" ? (
            <Alert
              severity="success"
              variant="filled"
              action={
                // TRIAGE-02 (IMP-02) — undo-based dismissal: the snackbar
                // carries the inverse, no confirmation ever blocks.
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    useUndoStackStore.getState().triggerUndo?.();
                    setSnack(null);
                  }}
                >
                  Undo
                </Button>
              }
              onClose={() => {
                setSnack(null);
              }}
            >
              Finding dismissed — Ctrl+Z to undo
            </Alert>
          ) : snack.kind === "bulkDismissSuccess" ? (
            <Alert
              severity="success"
              variant="filled"
              onClose={() => {
                setSnack(null);
              }}
            >
              {snack.count} findings dismissed
            </Alert>
          ) : snack.kind === "undismissSuccess" ? (
            <Alert
              severity="success"
              variant="filled"
              onClose={() => {
                setSnack(null);
              }}
            >
              Finding restored
            </Alert>
          ) : snack.kind === "mergeSuccess" ? (
            <Alert
              severity="success"
              variant="filled"
              onClose={() => {
                setSnack(null);
              }}
            >
              Merged into defect {snack.defectId}
            </Alert>
          ) : (
            <Alert
              severity="error"
              variant="filled"
              onClose={() => {
                setSnack(null);
              }}
            >
              {snack.action === "promote"
                ? "Promote"
                : snack.action === "dismiss"
                  ? "Dismiss"
                  : snack.action === "bulkDismiss"
                    ? "Bulk dismiss"
                    : snack.action === "undismiss"
                      ? "Undismiss"
                      : "Merge"}{" "}
              failed (errorId: {snack.errorId})
            </Alert>
          )}
        </Snackbar>
      ) : null}

      <Popover
        open={Boolean(dismissPopover)}
        anchorEl={dismissPopover?.anchor ?? null}
        onClose={closeDismissPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            // Trap pointer events so the parent ListItemButton doesn't fire
            // a select-row when the user clicks inside the popover.
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            },
          },
        }}
      >
        <Stack
          spacing={1}
          sx={{ p: 2, minWidth: 280, maxWidth: 360 }}
          role="dialog"
          aria-label="Dismiss finding"
        >
          <Typography variant="subtitle2">Dismiss reason (optional)</Typography>
          {/* TRIAGE-02 (IMP-02) — preset chips: one click = dismiss with
              that reason, no typing. */}
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {DISMISS_REASON_PRESETS.map((preset) => (
              <Chip
                key={preset}
                label={preset}
                size="small"
                onClick={() => {
                  submitDismiss(preset);
                }}
                data-testid={`dismiss-reason-chip-${preset.replace(/\s+/g, "-").toLowerCase()}`}
              />
            ))}
          </Stack>
          <TextField
            inputRef={dismissInputRef}
            value={dismissReason}
            onChange={(e) => {
              setDismissReason(e.target.value);
            }}
            onKeyDown={(e) => {
              // IMP-02 — Ctrl+Enter submits the free-text reason.
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                submitDismiss();
              }
            }}
            size="small"
            placeholder="Custom reason… (Ctrl+Enter to dismiss)"
            multiline
            minRows={2}
            maxRows={4}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={closeDismissPopover}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                submitDismiss();
              }}
            >
              Dismiss
            </Button>
          </Stack>
        </Stack>
      </Popover>

      <Popover
        open={Boolean(mergePopover)}
        anchorEl={mergePopover}
        onClose={() => {
          setMergePopover(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack
          spacing={1.5}
          sx={{ p: 2, minWidth: 280 }}
          role="dialog"
          aria-label="Confirm merge findings"
        >
          <Typography variant="subtitle2">
            Create one defect from {selectedForMerge.size} findings?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All selected findings will flip to converted and link back to the
            new defect.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setMergePopover(null);
              }}
            >
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={submitMerge}>
              Confirm merge
            </Button>
          </Stack>
        </Stack>
      </Popover>

      {/* R58-L-11 AC3 — bulk dismiss confirm popover. */}
      <Popover
        open={Boolean(bulkDismissPopover)}
        anchorEl={bulkDismissPopover}
        onClose={closeBulkDismissPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            },
          },
        }}
      >
        <Stack
          spacing={1.5}
          sx={{ p: 2, minWidth: 320 }}
          role="dialog"
          aria-label="Bulk dismiss findings"
          data-testid="findings-bulk-dismiss-dialog"
        >
          <Typography variant="subtitle2">
            Dismiss {bulkDismissableIds.length} selected findings?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Provide a shared dismiss reason (required for bulk actions).
          </Typography>
          {/* TRIAGE-02 (IMP-02 / Q4 direction) — preset chips satisfy the
              required bulk reason in one click; free text stays optional. */}
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {DISMISS_REASON_PRESETS.map((preset) => (
              <Chip
                key={preset}
                label={preset}
                size="small"
                variant={bulkDismissReason === preset ? "filled" : "outlined"}
                color={bulkDismissReason === preset ? "primary" : "default"}
                onClick={() => {
                  setBulkDismissReason(preset);
                }}
                data-testid={`bulk-dismiss-reason-chip-${preset.replace(/\s+/g, "-").toLowerCase()}`}
              />
            ))}
          </Stack>
          <TextField
            inputRef={bulkDismissInputRef}
            value={bulkDismissReason}
            onChange={(e) => {
              setBulkDismissReason(e.target.value);
            }}
            size="small"
            placeholder="e.g. false positive — sun glare"
            multiline
            minRows={2}
            maxRows={4}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            required
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={closeBulkDismissPopover}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={submitBulkDismiss}
              disabled={
                bulkDismissReason.trim().length === 0 ||
                bulkDismissableIds.length === 0
              }
            >
              Dismiss selected
            </Button>
          </Stack>
        </Stack>
      </Popover>

      {/* R58-L-11 AC5 — undismiss confirm popover. */}
      <Popover
        open={Boolean(undismissConfirm)}
        anchorEl={undismissConfirm?.anchor ?? null}
        onClose={closeUndismissConfirm}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            },
          },
        }}
      >
        <Stack
          spacing={1.5}
          sx={{ p: 2, minWidth: 280 }}
          role="dialog"
          aria-label="Restore dismissed finding"
          data-testid="findings-undismiss-dialog"
        >
          <Typography variant="subtitle2">Restore this finding?</Typography>
          <Typography variant="body2" color="text.secondary">
            {undismissConfirm
              ? `"${undismissConfirm.aiClassName}" will appear again under Pending.`
              : ""}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={closeUndismissConfirm}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={submitUndismiss}>
              Restore
            </Button>
          </Stack>
        </Stack>
      </Popover>

      {/* Story 020 — Reclassify context menu (AI-finding source). */}
      <Menu
        open={Boolean(ctxMenu)}
        onClose={() => {
          setCtxMenu(null);
        }}
        anchorReference="anchorPosition"
        {...(ctxMenu ? { anchorPosition: ctxMenu.anchorPos } : {})}
      >
        <Tooltip
          title={denyTooltip ?? ""}
          disableHoverListener={canEdit}
          disableFocusListener={canEdit}
          disableTouchListener={canEdit}
          placement="left"
        >
          <Box component="span">
            <MenuItem
              disabled={!canEdit}
              aria-disabled={!canEdit}
              onClick={() => {
                if (!canEdit) return;
                if (!ctxMenu) return;
                const { rowEl, findingId, aiClassName, classUuid } = ctxMenu;
                setPopoverAnchor(rowEl);
                setPopoverSelection({
                  ids: [findingId],
                  aiClassName,
                  classUuid,
                });
                setCtxMenu(null);
              }}
            >
              Reclassify
            </MenuItem>
          </Box>
        </Tooltip>
      </Menu>

      <CataloguePopover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => {
          setPopoverAnchor(null);
          setPopoverSelection({
            ids: [],
            aiClassName: undefined,
            classUuid: undefined,
          });
        }}
        inspectionId={inspectionId}
        source="aiFinding"
        selection={{
          ids: popoverSelection.ids,
          ...(popoverSelection.aiClassName
            ? { aiClassName: popoverSelection.aiClassName }
            : {}),
          ...(popoverSelection.classUuid
            ? { classUuid: popoverSelection.classUuid }
            : {}),
        }}
      />
    </Paper>
  );
}
